/**
 * The two scraps of behaviour a class page needs beyond the theme's own.
 *
 * Attendee fields that follow the quantity. The buy form's quantity input
 * says how many spots are being booked; this keeps exactly that many name
 * fields on the page, preserving anything already typed when the count
 * changes. The fields live outside the form and associate via form=, so
 * FormData picks them up and required blocks the submit while any are blank.
 *
 * A guard against booking the past. Shopify preselects the first *available*
 * variant, and a finished session can still be available if nobody zeroed
 * its stock. The visible rows exclude past sessions, so when the form's
 * variant is not among them, the first future row is clicked -- one synthetic
 * click that runs the theme's whole selection flow, price and URL included.
 *
 * The quantity is also capped at the chosen session's remaining spots
 * (data-remaining on the row), so eight-plus-one cannot be asked for and
 * bounced at the cart instead.
 */

const escapeHtml = (s) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class ClassAttendees extends HTMLElement {
  connectedCallback() {
    // The form lives elsewhere in the section; everything is looked up late
    // because the section morphs on variant change and nodes are replaced.
    this.formId = this.dataset.formId ?? '';
    this.sync();
  }

  get quantityInput() {
    const form = document.getElementById(this.formId);
    return form ? form.querySelector('input[name="quantity"]') : null;
  }

  count() {
    const input = this.quantityInput;
    const q = parseInt(input?.value ?? '1', 10);
    // The ceiling is whatever the session has left (the input's max, kept by
    // clampQuantity); 12 is only a backstop against a runaway value when no
    // max is set, not a class-size rule.
    const cap = parseInt(input?.max ?? '', 10);
    const limit = Number.isFinite(cap) && cap > 0 ? cap : 12;
    return Number.isFinite(q) && q > 0 ? Math.min(q, limit) : 1;
  }

  sync() {
    const wrap = this.querySelector('[data-attendee-fields]');
    if (!wrap) return;
    const want = this.count();
    const have = wrap.children.length;

    for (let n = have; n > want; n--) wrap.lastElementChild?.remove();
    for (let n = have; n < want; n++) {
      const i = n + 1;
      const label = document.createElement('label');
      label.className = 'class-attendees__field';
      label.innerHTML =
        `<span class="class-attendees__label">Attendee ${i} name</span>` +
        `<input type="text" name="properties[Attendee ${i} name]" form="${escapeHtml(this.formId)}" ` +
        `required autocomplete="off" placeholder="Full name">`;
      wrap.append(label);
    }
  }
}

if (!customElements.get('class-attendees')) customElements.define('class-attendees', ClassAttendees);

/** The session rows' picker on this page, if any. */
const picker = () => document.querySelector('variant-picker.class-sessions');

/** Cap the buy form's quantity at the selected session's remaining spots. */
function clampQuantity() {
  const p = picker();
  const attendees = document.querySelector('class-attendees');
  if (!p || !(attendees instanceof ClassAttendees)) return;
  const chosen = p.querySelector('input[type="radio"]:checked');
  const input = attendees.quantityInput;
  if (!chosen || !input) return;

  const remaining = parseInt(chosen.dataset.remaining ?? '', 10);
  if (Number.isFinite(remaining) && remaining > 0) {
    input.max = String(remaining);
    if (parseInt(input.value, 10) > remaining) {
      input.value = String(remaining);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  } else {
    input.removeAttribute('max');
  }
}

// Delegated, so it survives the section being morphed out from under us.
document.addEventListener('change', (event) => {
  const t = event.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (t.name === 'quantity') document.querySelectorAll('class-attendees').forEach((a) => a.sync?.());
  if (t.type === 'radio' && t.closest('variant-picker.class-sessions')) clampQuantity();
});
document.addEventListener('input', (event) => {
  const t = event.target;
  if (t instanceof HTMLInputElement && t.name === 'quantity')
    document.querySelectorAll('class-attendees').forEach((a) => a.sync?.());
});
// The theme's +/- buttons never fire input or change: quantity-selector.js
// calls stepUp()/stepDown() on the input, which alters the value silently,
// and announces it with this event of its own instead. Without listening for
// it, clicking "+" booked two spots with one name box -- caught in testing.
document.addEventListener('quantity-selector:update', () => {
  document.querySelectorAll('class-attendees').forEach((a) => a.sync?.());
});

function guardSelection() {
  const p = picker();
  if (!p) return;
  const rows = [...p.querySelectorAll('input[type="radio"]')];
  if (!rows.length) return;

  const form = document.getElementById(p.closest('section, .shopify-section')?.querySelector('product-form-component form')?.id ?? '')
    ?? document.querySelector('product-form-component form');
  const formVariant = form?.querySelector('input[name="id"]')?.value ?? '';
  const checked = rows.find((r) => r.checked);

  if (checked && checked.dataset.variantId === formVariant) {
    clampQuantity();
    return;
  }
  // The form points at a session these rows do not offer (a past one), or
  // nothing is selected at all. Choose the first bookable future session the
  // way a person would, so every listener runs.
  const target = checked ?? rows.find((r) => !r.disabled);
  if (target) {
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', guardSelection, { once: true });
} else {
  guardSelection();
}
